import { CheckBoxButtonsGroup } from 'components/common-form/builder/form/node/field/CheckboxField';
import type { BackToBackOwnProps } from 'components/common-form/sidePanel/actions/trade/backToBack/shared';
import { AmountWithUnitAndLabel } from 'components/shared/Amount/Amount';
import { FieldErrorWrapper } from 'components/shared/FormGroup/ErrorMessages';
import { WithFormLabel } from 'components/shared/WithLabel/WithLabel';
import { connect } from 'react-redux';
import { getQuoteCurrency, getTradeConfirmation } from 'state/selectors/common';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import { tradeConfirmationSetBackToBackFieldValue } from 'state/uiPanelState/tradeConfirmation/tradeConfirm.action';
import type {
  TradeConfirmationBackToBack,
  TradeConfirmationBackToBackFields,
} from 'state/uiPanelState/tradeConfirmation/tradeConfirm.model';

type StateProps = Pick<
  TradeConfirmationBackToBack,
  'fileCode' | 'noteNominal' | 'isDeltaAdjusted' | 'isDelayedBackToBack'
> & {
  currency?: string;
};
type DispatchProps = {
  setFileCode: (fileCode?: string) => void;
  setIsDelayedBackToBack: (isDelayedBackToBack?: boolean) => void;
  setNoteNominal: (noteNominal?: number) => void;
  setIsDeltaAdjusted: (isDeltaAdjusted: boolean | null) => void;
};
type HedgingTradeWorkflowProps = StateProps & DispatchProps;

export function HedgingTradeWorkflow({
  setFileCode,
  fileCode,
  currency,
  noteNominal,
  isDeltaAdjusted,
  isDelayedBackToBack,
  setNoteNominal,
  setIsDelayedBackToBack,
  setIsDeltaAdjusted,
}: HedgingTradeWorkflowProps) {
  return (
    <>
      <FieldErrorWrapper<TradeConfirmationBackToBackFields> inputId="fileCode">
        <WithFormLabel className="flex-wrap my-1" id="fileCode" useFlex labelKey="form.BackToBackFileCode">
          <input
            readOnly={fileCode?.isReadonly}
            id="fileCode"
            className="form-control"
            type="text"
            defaultValue={fileCode?.value}
            onBlur={e => setFileCode(e.target.value)}
          />
        </WithFormLabel>
      </FieldErrorWrapper>
      <FieldErrorWrapper<TradeConfirmationBackToBackFields> inputId="noteNominal">
        <AmountWithUnitAndLabel
          useFlex
          value={noteNominal?.value}
          isReadOnly={noteNominal?.isReadonly}
          className="flex-wrap  my-1"
          id="noteNominal"
          labelKey="form.NoteNominal"
          unit={currency}
          onChange={setNoteNominal}
        />
      </FieldErrorWrapper>
      <FieldErrorWrapper<TradeConfirmationBackToBackFields> inputId="isDeltaAdjusted">
        <WithFormLabel className="flex-wrap  my-1" useFlex id="isDeltaAdjusted" labelKey="form.IsDeltaAdjusted">
          <CheckBoxButtonsGroup
            id="isDeltaAdjusted"
            isNullable={true}
            className="w-100 "
            isDisabled={isDeltaAdjusted?.isReadonly}
            value={isDeltaAdjusted?.value ?? null}
            translationPrefix="form"
            onChange={setIsDeltaAdjusted}
          />
        </WithFormLabel>
      </FieldErrorWrapper>
      <FieldErrorWrapper<TradeConfirmationBackToBackFields> inputId="isDelayedBackToBack">
        <WithFormLabel className="flex-wrap my-1" useFlex id="isDelayedBackToBack" labelKey="form.IsDelayedBackToBack">
          <CheckBoxButtonsGroup
            id="isDelayedBackToBack"
            isNullable={true}
            className="w-100 "
            isDisabled={isDelayedBackToBack?.isReadonly}
            value={isDelayedBackToBack?.value ?? null}
            translationPrefix="form"
            onChange={value => setIsDelayedBackToBack(value ?? undefined)}
          />
        </WithFormLabel>
      </FieldErrorWrapper>
    </>
  );
}

const mapDispatchToProps = (dispatch: ThunkDispatch, { quoteId }: BackToBackOwnProps): DispatchProps => ({
  setFileCode(fileCode) {
    dispatch(tradeConfirmationSetBackToBackFieldValue(quoteId, 'fileCode', fileCode));
  },
  setIsDelayedBackToBack(isDelayedBackToBack) {
    dispatch(tradeConfirmationSetBackToBackFieldValue(quoteId, 'isDelayedBackToBack', isDelayedBackToBack));
  },
  setNoteNominal(noteNominal) {
    dispatch(tradeConfirmationSetBackToBackFieldValue(quoteId, 'noteNominal', noteNominal));
  },
  setIsDeltaAdjusted(isDeltaAdjusted) {
    dispatch(tradeConfirmationSetBackToBackFieldValue(quoteId, 'isDeltaAdjusted', isDeltaAdjusted ?? undefined));
  },
});
const mapStateToProps = (state: AppState, { formId, workspace }: BackToBackOwnProps): StateProps => {
  const tradeConfirmation = getTradeConfirmation(state, workspace, formId);

  return {
    currency: getQuoteCurrency(state, workspace, formId),
    fileCode: tradeConfirmation.fileCode,
    isDeltaAdjusted: tradeConfirmation.isDeltaAdjusted,
    isDelayedBackToBack: tradeConfirmation.isDelayedBackToBack,
    noteNominal: tradeConfirmation.noteNominal,
  };
};
export const ConnectedHedgingTradeWorkflow = connect(mapStateToProps, mapDispatchToProps)(HedgingTradeWorkflow);
